"use client";
// Booking-page vehicle gallery: a scroll-snap slider with prev/next arrows,
// dot indicators and a thumbnail strip. Degrades to a single static photo when
// the car has only one image.
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import styles from "./car-gallery.module.css";

export default function CarGallery({ images = [], alt = "" }) {
    const list = Array.isArray(images) ? images.filter(Boolean) : [];
    const trackRef = useRef(null);
    const [active, setActive] = useState(0);

    const scrollTo = useCallback((i) => {
        const track = trackRef.current;
        if (!track) return;
        const clamped = Math.max(0, Math.min(list.length - 1, i));
        track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
    }, [list.length]);

    // keep the active dot in sync as the visitor swipes/scrolls the track
    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        let raf = 0;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const i = Math.round(track.scrollLeft / track.clientWidth);
                setActive((prev) => (prev === i ? prev : i));
            });
        };
        track.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            track.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(raf);
        };
    }, []);

    if (!list.length) {
        return (
            <div className={styles.media}>
                <span className={styles.noPhoto} aria-hidden>🚗</span>
            </div>
        );
    }

    const many = list.length > 1;

    return (
        <div className={styles.gallery}>
            <div className={styles.stage}>
                <div
                    className={styles.track}
                    ref={trackRef}
                    tabIndex={many ? 0 : -1}
                    aria-label={many ? `${alt} — ${list.length} photos` : alt}
                    onKeyDown={(e) => {
                        if (!many) return;
                        if (e.key === "ArrowRight") { e.preventDefault(); scrollTo(active + 1); }
                        if (e.key === "ArrowLeft") { e.preventDefault(); scrollTo(active - 1); }
                    }}
                >
                    {list.map((src, i) => (
                        <div className={styles.slide} key={i}>
                            <Image
                                src={src}
                                alt={i === 0 ? alt : ""}
                                fill
                                className={styles.photo}
                                priority={i === 0}
                                unoptimized
                            />
                        </div>
                    ))}
                </div>

                {many && (
                    <>
                        <button
                            type="button"
                            className={`${styles.nav} ${styles.prev}`}
                            onClick={() => scrollTo(active - 1)}
                            disabled={active === 0}
                            aria-label="Previous photo"
                        >
                            <LuChevronLeft aria-hidden />
                        </button>
                        <button
                            type="button"
                            className={`${styles.nav} ${styles.next}`}
                            onClick={() => scrollTo(active + 1)}
                            disabled={active === list.length - 1}
                            aria-label="Next photo"
                        >
                            <LuChevronRight aria-hidden />
                        </button>
                        <span className={styles.counter}>{active + 1} / {list.length}</span>
                    </>
                )}
            </div>

            {many && (
                <div className={styles.thumbs}>
                    {list.map((src, i) => (
                        <button
                            type="button"
                            key={i}
                            className={`${styles.thumb} ${i === active ? styles.thumbOn : ""}`}
                            onClick={() => scrollTo(i)}
                            aria-label={`Photo ${i + 1}`}
                            aria-current={i === active}
                        >
                            <Image src={src} alt="" fill className={styles.thumbImg} unoptimized />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
